import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import useCurrentUser from "@/hooks/useCurrentUser";
import {
  ASSUMED_USER_CLEARED_EVENT,
  ASSUMED_USER_STORAGE_KEY,
  clearAssumedUserStorage,
  getInternalReturnUrl,
  persistAssumedUser,
  readAssumedUser,
  type AssumedUser
} from "./storage";
import type { ImpersonationFailure } from "@/providers/GraphqlProvider/errors";

export type AssumedUserInput = Omit<AssumedUser, "returnUrl"> & {
  returnUrl?: string;
};

type AssumeUserContextValue = {
  assumedUser: AssumedUser | null;
  authorizationSubjectId: string | null;
  impersonationError: ImpersonationFailure | null;
  sessionKey: number;
  startAssumingUser: (user: AssumedUserInput) => void;
  confirmAssumedUserAuthorization: () => void;
  stopAssumingUser: () => void;
  clearAssumedUserSession: (expectedUserId?: string) => void;
  reportImpersonationError: (error: ImpersonationFailure, expectedUserId?: string) => void;
};

const defaultContext: AssumeUserContextValue = {
  assumedUser: null,
  authorizationSubjectId: null,
  impersonationError: null,
  sessionKey: 0,
  startAssumingUser: () => {},
  confirmAssumedUserAuthorization: () => {},
  stopAssumingUser: () => {},
  clearAssumedUserSession: () => {},
  reportImpersonationError: () => {}
};

export const AssumeUserContext = createContext<AssumeUserContextValue>(defaultContext);

const currentReturnUrl = (): string => `${window.location.pathname}${window.location.search}${window.location.hash}`;

export function useAssumeUser(): AssumeUserContextValue {
  return useContext(AssumeUserContext);
}

export default function AssumeUserProvider({ children }: { children: ReactNode }) {
  const { user } = useCurrentUser();
  const authenticatedUserSub = user.sub;
  const assumedUserRef = useRef<AssumedUser | null>(null);
  const [assumedUser, setAssumedUser] = useState<AssumedUser | null>(() => {
    const initialAssumedUser = readAssumedUser(authenticatedUserSub);
    assumedUserRef.current = initialAssumedUser;
    return initialAssumedUser;
  });
  const [authorizationSubjectId, setAuthorizationSubjectId] = useState<string | null>(
    () => assumedUserRef.current?.id ?? null
  );
  const [assumedUserOwner, setAssumedUserOwner] = useState(authenticatedUserSub);
  const [impersonationError, setImpersonationError] = useState<ImpersonationFailure | null>(null);
  const [sessionKey, setSessionKey] = useState(0);

  const clearInMemoryAssumedUser = useCallback(() => {
    if (assumedUserRef.current === null) return;

    assumedUserRef.current = null;
    setAssumedUser(null);
    setAuthorizationSubjectId(null);
    setAssumedUserOwner(authenticatedUserSub);
    setSessionKey((currentKey) => currentKey + 1);
  }, [authenticatedUserSub]);

  useEffect(() => {
    if (assumedUserOwner !== authenticatedUserSub) {
      clearAssumedUserStorage({ notify: false });
      if (assumedUserRef.current !== null) {
        clearInMemoryAssumedUser();
      } else {
        setSessionKey((currentKey) => currentKey + 1);
      }
      setAssumedUserOwner(authenticatedUserSub);
      setImpersonationError(null);
    }
  }, [assumedUserOwner, authenticatedUserSub, clearInMemoryAssumedUser]);

  useEffect(() => {
    const handleAssumedUserCleared = () => {
      clearInMemoryAssumedUser();
    };
    const handleAssumedUserStorageChange = (event: StorageEvent) => {
      if (event.key !== null && event.key !== ASSUMED_USER_STORAGE_KEY) return;

      if (event.newValue === null) {
        clearInMemoryAssumedUser();
        return;
      }

      const nextAssumedUser = readAssumedUser(authenticatedUserSub);
      if (!nextAssumedUser) {
        clearInMemoryAssumedUser();
        return;
      }
      if (assumedUserRef.current?.id === nextAssumedUser.id) return;

      assumedUserRef.current = nextAssumedUser;
      setAssumedUser(nextAssumedUser);
      setAuthorizationSubjectId(nextAssumedUser.id);
      setAssumedUserOwner(authenticatedUserSub);
      setImpersonationError(null);
      setSessionKey((currentKey) => currentKey + 1);
    };

    window.addEventListener(ASSUMED_USER_CLEARED_EVENT, handleAssumedUserCleared);
    window.addEventListener("storage", handleAssumedUserStorageChange);
    return () => {
      window.removeEventListener(ASSUMED_USER_CLEARED_EVENT, handleAssumedUserCleared);
      window.removeEventListener("storage", handleAssumedUserStorageChange);
    };
  }, [authenticatedUserSub, clearInMemoryAssumedUser]);

  const reportImpersonationError = useCallback((error: ImpersonationFailure, expectedUserId?: string) => {
    if (expectedUserId && assumedUserRef.current?.id !== expectedUserId) return;

    setImpersonationError((currentError) => currentError ?? error);
  }, []);

  const startAssumingUser = useCallback(
    (user: AssumedUserInput) => {
      const nextAssumedUser: AssumedUser = {
        ...user,
        returnUrl: getInternalReturnUrl(user.returnUrl ?? currentReturnUrl())
      };

      persistAssumedUser(nextAssumedUser, authenticatedUserSub);
      assumedUserRef.current = nextAssumedUser;
      setImpersonationError(null);
      setAssumedUser(nextAssumedUser);
      setAssumedUserOwner(authenticatedUserSub);
    },
    [authenticatedUserSub]
  );

  const confirmAssumedUserAuthorization = useCallback(() => {
    const currentAssumedUser = assumedUserRef.current;
    if (!currentAssumedUser) return;

    setAuthorizationSubjectId(currentAssumedUser.id);
  }, []);

  const clearAssumedUserSession = useCallback(
    (expectedUserId?: string) => {
      const currentAssumedUser = assumedUserRef.current;
      if (expectedUserId && currentAssumedUser?.id !== expectedUserId) return;

      try {
        clearAssumedUserStorage({ notify: false });
      } finally {
        clearInMemoryAssumedUser();
      }
    },
    [clearInMemoryAssumedUser]
  );

  const stopAssumingUser = useCallback(() => {
    setImpersonationError(null);
    clearAssumedUserSession();
  }, [clearAssumedUserSession]);

  const activeAssumedUser = assumedUserOwner === authenticatedUserSub ? assumedUser : null;
  const value = useMemo(
    () => ({
      assumedUser: activeAssumedUser,
      authorizationSubjectId: assumedUserOwner === authenticatedUserSub ? authorizationSubjectId : null,
      impersonationError,
      sessionKey,
      startAssumingUser,
      confirmAssumedUserAuthorization,
      stopAssumingUser,
      clearAssumedUserSession,
      reportImpersonationError
    }),
    [
      activeAssumedUser,
      authorizationSubjectId,
      assumedUserOwner,
      authenticatedUserSub,
      impersonationError,
      sessionKey,
      startAssumingUser,
      confirmAssumedUserAuthorization,
      stopAssumingUser,
      clearAssumedUserSession,
      reportImpersonationError
    ]
  );

  return <AssumeUserContext.Provider value={value}>{children}</AssumeUserContext.Provider>;
}
